import { reactive } from "vue";

/**
 * Критический экран (/critical-modal): состояние в памяти + переход на маршрут.
 *
 * Состояние не кладём в query/localStorage, чтобы после перезагрузки страницы
 * не показывать устаревшую ошибку (guard маршрута редиректит на «/», если state.active === false).
 *
 * Действия кнопок (`action`, см. runCriticalAction):
 *  - 'reload'   — «Переподключиться»: полная перезагрузка приложения (заново авторизуется через Telegram);
 *  - 'relogin'  — «Перезайти»: то же, что reload (токен при старте всё равно сбрасывается);
 *  - 'close'    — «Закрыть»: Telegram.WebApp.close() (вне Telegram — reload).
 *
 * «Мягкого» выхода на главный экран нет намеренно: к моменту показа экрана WebSocket закрыт
 * окончательно (или токен недействителен), и без перезагрузки приложение осталось бы нерабочим.
 */

/** Предопределённые виды критических ошибок. */
export const CRITICAL = Object.freeze({
  SESSION_EXPIRED: {
    kind: "session",
    title: "Сессия истекла",
    message: "Время сессии вышло. Перезайдите, чтобы продолжить играть.",
    action: "relogin",
    actionLabel: "Перезайти",
  },
  REPLACED: {
    kind: "replaced",
    title: "Вы подключились с другого устройства",
    message: "Игра открыта в другом окне или на другом устройстве. Здесь соединение закрыто.",
    action: "reload",
    actionLabel: "Переподключиться",
  },
  OFFLINE: {
    kind: "offline",
    title: "Нет соединения с сервером",
    message: "Не удаётся связаться с сервером. Проверьте интернет и попробуйте переподключиться.",
    action: "reload",
    actionLabel: "Переподключиться",
  },
  GENERIC: {
    kind: "generic",
    title: "Что-то пошло не так",
    message: "Произошла непредвиденная ошибка. Попробуйте переподключиться.",
    action: "reload",
    actionLabel: "Переподключиться",
  },
});

/** Текущее состояние критического экрана (читает CriticalScreen.vue). */
export const criticalState = reactive({
  active: false,
  kind: "generic",
  title: "",
  message: "",
  action: "reload",
  actionLabel: "Переподключиться",
});

let routerRef = null;

/** Вызывается один раз из main.js, чтобы сервисы могли навигировать без циклических импортов. */
export function registerCriticalRouter(router) {
  routerRef = router;
}

/**
 * Показать критический экран.
 * @param {object} preset один из CRITICAL.* или произвольный {title, message, action, actionLabel}
 * @param {object} [overrides] частичная замена полей пресета (например, свой message)
 */
export function openCritical(preset = CRITICAL.GENERIC, overrides = {}) {
  Object.assign(criticalState, CRITICAL.GENERIC, preset, overrides, { active: true });
  if (routerRef && routerRef.currentRoute.value.path !== "/critical-modal") {
    routerRef.replace({ path: "/critical-modal", query: { kind: criticalState.kind } }).catch(() => {});
  }
}

/**
 * Выполнить действие кнопки критического экрана.
 * @param {'reload'|'relogin'|'close'} [action] по умолчанию — действие основной кнопки пресета
 */
export function runCriticalAction(action = criticalState.action) {
  if (action === "close") {
    const webApp = window.Telegram && window.Telegram.WebApp;
    if (webApp && webApp.initData && typeof webApp.close === "function") {
      webApp.close();
      return;
    }
  }
  // reload / relogin (и «Закрыть» вне Telegram): полная перезагрузка. Guard маршрута после
  // перезагрузки отправит на «/», telegram-web-app.js восстановит initData из sessionStorage.
  window.location.reload();
}
